import React from 'react';

import i18n from 'util/i18n';
import { Summary } from 'components/Summary';

import { SUBSTANCE_USE, SUBSTANCE_USE_DRUGS_PURCHASE } from 'config/formSections/substanceUse';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString } from 'helpers/text';
import { isPVQForm } from 'helpers/form';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import DrugInvolvement from './DrugInvolvementItem';

const sectionConfig = {
  key: SUBSTANCE_USE_DRUGS_PURCHASE.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_DRUGS_PURCHASE.name,
  storeKey: SUBSTANCE_USE_DRUGS_PURCHASE.storeKey,
};

export class DrugInvolvements extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateInvolved = this.updateInvolved.bind(this);
    this.updateList = this.updateList.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      Involved: this.props.Involved,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateInvolved = (values) => {
    this.update({
      Involved: values,
      List: values.value === 'Yes' ? this.props.List : [],
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    let drug = (o.DrugType || {}).value;
    if (drug === 'Other') {
      drug = ((o.DrugType || {}).DrugTypeOther || {}).value;
    }

    return Summary({
      type: i18n.t('substance.drugs.involvement.collection.itemType'),
      index,
      left: drug,
      right: null,
      placeholder: i18n.t('substance.drugs.involvement.collection.summary'),
    });
  };

  render() {
    const {
      formType,
      requireDrugWhileSafety,
      requireDrugWithClearance,
      requireDrugInFuture,
      errors,
    } = this.props;
    const formTypeConfig = formType && formConfig[formType];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_USE_YEARS;
    const numberOfYearsString = getNumberOfYearsString(years);

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div
        className="section-content drug-involvements"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_PURCHASE.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.drugs.purchase')}</h1>
        <Branch
          name="Involved"
          attribute="Involved"
          label={i18n.t('substance.pvq.drugs.heading.drugInvolvement', { numberOfYearsString })}
          labelSize="h4"
          className="involved"
          {...this.props.Involved}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateInvolved}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Show when={this.props.Involved.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.drugs.involvement.collection.description')}
            appendTitle={i18n.t('substance.pvq.drugs.involvement.collection.appendTitle')}
            appendLabel={i18n.t('substance.pvq.drugs.involvement.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <DrugInvolvement
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireDrugWhileSafety={requireDrugWhileSafety}
              requireDrugWithClearance={requireDrugWithClearance}
              requireDrugInFuture={requireDrugInFuture}
              formType={this.props.formType}
            />
          </Accordion>
        </Show>
      </div>

    ) : (
      <div
        className="section-content drug-involvements"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_PURCHASE.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.drugs.purchase')}</h1>
        <Branch
          name="Involved"
          attribute="Involved"
          label={i18n.t('substance.drugs.heading.drugInvolvement', { numberOfYearsString })}
          labelSize="h4"
          className="involved"
          {...this.props.Involved}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateInvolved}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        <Show when={this.props.Involved.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.drugs.involvement.collection.description')}
            appendTitle={i18n.t('substance.drugs.involvement.collection.appendTitle')}
            appendLabel={i18n.t('substance.drugs.involvement.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <DrugInvolvement
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireDrugWhileSafety={requireDrugWhileSafety}
              requireDrugWithClearance={requireDrugWithClearance}
              requireDrugInFuture={requireDrugInFuture}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

DrugInvolvements.defaultProps = {
  Involved: {},
  List: { items: [], branch: {} },
  Comments: {},
  SummaryComments: {},
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'drugs/purchase',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
  formType: '',
};

export default connectSubsection(DrugInvolvements, sectionConfig);
